import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextareaAutosize,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  Link,
  Container,
  FormControl,
  InputLabel,
  Select,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Gavel as GavelIcon,
  GetApp as DownloadIcon,
  AssignmentTurnedIn as ResolveIcon,
  WarningAmber as EscalateIcon,
  CheckCircle as CompleteIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import disputeService, { Dispute, DisputeDetail, ResolveDisputeParams } from '../services/api/disputeService';

interface DisputeStats {
  openDisputes: number;
  resolvedThisWeek: number;
  resolutionRate: number;
  pendingDisputes: number;
  statusCounts: {
    open: number;
    under_review: number;
    resolved: number;
    escalated: number;
    closed: number;
  };
}

export default function DisputesPage() {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [stats, setStats] = useState<DisputeStats>({
    openDisputes: 0,
    resolvedThisWeek: 0,
    resolutionRate: 0,
    pendingDisputes: 0,
    statusCounts: {
      open: 0,
      under_review: 0,
      resolved: 0,
      escalated: 0,
      closed: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [totalDisputes, setTotalDisputes] = useState(0);
  const [selectedDispute, setSelectedDispute] = useState<DisputeDetail | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'resolve' | 'escalate' | 'close';
    disputeId: string;
  } | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Resolution dialog fields
  const [resolutionType, setResolutionType] = useState<ResolveDisputeParams['resolution_type']>('full_refund');
  const [artisanPayout, setArtisanPayout] = useState('');
  const [customerRefund, setCustomerRefund] = useState('');

  useEffect(() => {
    loadDisputes();
  }, [paginationModel, searchQuery, statusFilter]);

  const calculateStats = (disputeList: Dispute[]) => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const statusCounts = {
      open: disputeList.filter((d) => d.status === 'open').length,
      under_review: disputeList.filter((d) => d.status === 'under_review').length,
      resolved: disputeList.filter((d) => d.status === 'resolved').length,
      escalated: disputeList.filter((d) => d.status === 'escalated').length,
      closed: disputeList.filter((d) => d.status === 'closed').length,
    };

    const resolvedThisWeek = disputeList.filter(
      (d) => d.status === 'resolved' && new Date(d.created_at) >= oneWeekAgo
    ).length;

    const openDisputes = statusCounts.open + statusCounts.under_review;
    const pendingDisputes = statusCounts.open;
    const resolutionRate =
      disputeList.length > 0
        ? Math.round(
            ((statusCounts.resolved + statusCounts.closed) / disputeList.length) * 100
          )
        : 0;

    return {
      openDisputes,
      resolvedThisWeek,
      resolutionRate,
      pendingDisputes,
      statusCounts,
    };
  };

  const handleExportCSV = () => {
    const rows = [
      'ID,Status,Reason,Job,Reporter,Created',
      ...disputes.map((d: any) =>
        `${d.id},${d.status},${(d.reason ?? '').replace(/,/g, ';')},${d.job_id ?? ''},${d.reporter?.email ?? ''},${d.created_at ?? ''}`
      ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `disputes_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const loadDisputes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await disputeService.getDisputes(statusFilter || undefined);

      let filteredDisputes = data.disputes || [];

      // Apply search filter
      if (searchQuery) {
        filteredDisputes = filteredDisputes.filter(
          (d) =>
            d.job?.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            d.initiated_by?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            d.reason?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      // Calculate stats
      const disputeStats = calculateStats(filteredDisputes);
      setStats(disputeStats);
      setTotalDisputes(filteredDisputes.length);

      // Apply pagination
      const start = paginationModel.page * paginationModel.pageSize;
      const paginatedDisputes = filteredDisputes.slice(
        start,
        start + paginationModel.pageSize
      );

      setDisputes(paginatedDisputes);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load disputes');
      console.error('Error loading disputes:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  const handleOpenDialog = async (dispute: Dispute) => {
    try {
      setLoading(true);
      const detail = await disputeService.getDisputeDetail(dispute.id);
      setSelectedDispute(detail);
      setOpenDialog(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load dispute details');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDispute(null);
    setResolutionNotes('');
  };

  const handleOpenConfirmDialog = (
    type: 'resolve' | 'escalate' | 'close',
    disputeId: string
  ) => {
    setConfirmAction({ type, disputeId });
    setResolutionNotes('');
    setResolutionType('full_refund');
    setArtisanPayout('');
    setCustomerRefund('');
    setOpenConfirmDialog(true);
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    try {
      setActionLoading(true);

      if (confirmAction.type === 'escalate') {
        // Simple status update — no escrow movement
        await disputeService.updateDisputeStatus(
          confirmAction.disputeId,
          'escalated',
          resolutionNotes || undefined
        );
      } else if (confirmAction.type === 'resolve') {
        // Full resolution with escrow control
        const params: ResolveDisputeParams = {
          resolution_type: resolutionType,
          admin_notes: resolutionNotes || undefined,
        };
        if (resolutionType === 'partial_split') {
          if (artisanPayout) params.artisan_payout = Number(artisanPayout);
          if (customerRefund) params.customer_refund = Number(customerRefund);
        }
        await disputeService.resolveDispute(confirmAction.disputeId, params);
      } else if (confirmAction.type === 'close') {
        await disputeService.updateDisputeStatus(
          confirmAction.disputeId,
          'closed',
          resolutionNotes || undefined
        );
      }

      setOpenConfirmDialog(false);
      setConfirmAction(null);
      handleCloseDialog();
      await loadDisputes();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status: string): 'error' | 'warning' | 'success' | 'info' | 'default' => {
    const colors: Record<string, 'error' | 'warning' | 'success' | 'info' | 'default'> = {
      open: 'error',
      under_review: 'warning',
      resolved: 'success',
      escalated: 'info',
      closed: 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      open: 'Open',
      under_review: 'Under Review',
      resolved: 'Resolved',
      escalated: 'Escalated',
      closed: 'Closed',
    };
    return labels[status] || status;
  };

  // DataTable columns definition
  const columns: GridColDef[] = [
    {
      field: 'job',
      headerName: 'Job Title',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value?.title || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Budget: {params.value?.budget ? formatCurrency(params.value.budget) : 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'initiated_by',
      headerName: 'Initiated By',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">{params.value?.name || 'Unknown'}</Typography>
      ),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || 'N/A'}</Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value)}
          variant="filled"
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={<GavelIcon />}
          onClick={() => handleOpenDialog(params.row)}
        >
          Review
        </Button>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Title */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <GavelIcon sx={{ fontSize: 32 }} />
          Disputes Management
        </Typography>
      </Box>

      {/* Metric Cards - Top Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Open Disputes"
            value={stats.openDisputes}
            icon={<GavelIcon />}
            color="error"
            trend={stats.openDisputes > 10 ? 2 : -3}
            trendDirection={stats.openDisputes > 10 ? 'up' : 'down'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Resolved This Week"
            value={stats.resolvedThisWeek}
            icon={<CompleteIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Resolution Rate"
            value={`${stats.resolutionRate}%`}
            subtitle="Disputes resolved"
            icon={<ResolveIcon />}
            color="info"
            trend={stats.resolutionRate > 70 ? 5 : -2}
            trendDirection={stats.resolutionRate > 70 ? 'up' : 'down'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Action"
            value={stats.pendingDisputes}
            subtitle="Awaiting review"
            icon={<EscalateIcon />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Status Overview */}
      <Card sx={{ mb: 4, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Status Overview
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Chip
            label={`Open: ${stats.statusCounts.open}`}
            color="error"
            variant="filled"
          />
          <Chip
            label={`Under Review: ${stats.statusCounts.under_review}`}
            color="warning"
            variant="filled"
          />
          <Chip
            label={`Resolved: ${stats.statusCounts.resolved}`}
            color="success"
            variant="filled"
          />
          <Chip
            label={`Escalated: ${stats.statusCounts.escalated}`}
            color="info"
            variant="filled"
          />
          <Chip
            label={`Closed: ${stats.statusCounts.closed}`}
            variant="filled"
          />
        </Box>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filter Card */}
      <Card sx={{ mb: 3, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by job, initiator, or reason..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPaginationModel({ ...paginationModel, page: 0 });
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPaginationModel({ ...paginationModel, page: 0 });
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="escalated">Escalated</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('');
                setPaginationModel({ ...paginationModel, page: 0 });
              }}
            >
              Clear Filters
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* DataTable */}
      <DataTable
        title="Disputes List"
        columns={columns}
        rows={disputes}
        rowCount={totalDisputes}
        paginationModel={paginationModel}
        onPaginationChange={setPaginationModel}
        loading={loading}
        error={error}
        searchPlaceholder="Search disputes..."
      />

      {/* Dispute Detail Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <GavelIcon />
          Dispute Details
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {selectedDispute && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Job Title
                </Typography>
                <Typography variant="body1">{selectedDispute.job.title}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Client
                </Typography>
                <Typography variant="body1">{selectedDispute.job.client.name}</Typography>
              </Grid>

              {selectedDispute.job.artisan && (
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Artisan
                  </Typography>
                  <Typography variant="body1">{selectedDispute.job.artisan.name}</Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Initiated By
                </Typography>
                <Typography variant="body1">{selectedDispute.initiated_by.name}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Reason
                </Typography>
                <Typography variant="body1">{selectedDispute.reason}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body2">{selectedDispute.description}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Budget
                </Typography>
                <Typography variant="body1">
                  ₦{selectedDispute.job.budget.toLocaleString()}
                </Typography>
              </Grid>

              {selectedDispute.job.escrow_amount != null && (
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Escrow Held
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 700 }}>
                    ₦{selectedDispute.job.escrow_amount.toLocaleString()}
                  </Typography>
                </Grid>
              )}

              {selectedDispute.admin_notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Admin Notes</Typography>
                  <Typography variant="body2">{selectedDispute.admin_notes}</Typography>
                </Grid>
              )}

              {selectedDispute.evidence_urls && selectedDispute.evidence_urls.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Evidence ({selectedDispute.evidence_urls.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedDispute.evidence_urls.map((url, idx) => (
                      <Link
                        key={idx}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: '0.875rem' }}
                      >
                        <DownloadIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Evidence {idx + 1}
                      </Link>
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={getStatusLabel(selectedDispute.status)}
                  color={getStatusColor(selectedDispute.status) as any}
                  variant="filled"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={handleCloseDialog}>Close</Button>

          {/* Open → mark under review */}
          {selectedDispute?.status === 'open' && (
            <Button
              variant="outlined"
              color="warning"
              onClick={async () => {
                try {
                  setActionLoading(true);
                  await disputeService.updateDisputeStatus(selectedDispute.id, 'under_review');
                  handleCloseDialog();
                  await loadDisputes();
                } catch (e: any) {
                  setError(e.response?.data?.message || 'Failed to update status');
                } finally {
                  setActionLoading(false);
                }
              }}
              disabled={actionLoading}
            >
              Mark Under Review
            </Button>
          )}

          {/* Open or under_review → escalate or resolve */}
          {selectedDispute && (selectedDispute.status === 'open' || selectedDispute.status === 'under_review') && (
            <>
              <Button
                color="info"
                variant="outlined"
                startIcon={<EscalateIcon />}
                onClick={() => handleOpenConfirmDialog('escalate', selectedDispute.id)}
              >
                Escalate
              </Button>
              <Button
                color="success"
                variant="contained"
                startIcon={<ResolveIcon />}
                onClick={() => handleOpenConfirmDialog('resolve', selectedDispute.id)}
              >
                Resolve &amp; Release Escrow
              </Button>
            </>
          )}

          {/* Escalated → resolve */}
          {selectedDispute?.status === 'escalated' && (
            <Button
              color="success"
              variant="contained"
              startIcon={<ResolveIcon />}
              onClick={() => handleOpenConfirmDialog('resolve', selectedDispute.id)}
            >
              Resolve &amp; Release Escrow
            </Button>
          )}

          {/* Resolved/escalated → close */}
          {selectedDispute && (selectedDispute.status === 'resolved' || selectedDispute.status === 'escalated') && (
            <Button
              color="warning"
              variant="outlined"
              onClick={() => handleOpenConfirmDialog('close', selectedDispute.id)}
            >
              Close Dispute
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation / Resolution Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => !actionLoading && setOpenConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {confirmAction?.type === 'resolve' && '⚖️ Resolve Dispute — Release Escrow'}
          {confirmAction?.type === 'escalate' && '🔺 Escalate Dispute'}
          {confirmAction?.type === 'close' && '🔒 Close Dispute'}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* Escrow context banner */}
          {confirmAction?.type === 'resolve' && selectedDispute?.job?.escrow_amount && (
            <Box sx={{ bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <MoneyIcon sx={{ color: '#15803d' }} />
              <Box>
                <Typography variant="body2" fontWeight={700} color="#15803d">
                  Escrow held: ₦{selectedDispute.job.escrow_amount.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Funds will be released according to your decision below.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Resolution type (resolve only) */}
          {confirmAction?.type === 'resolve' && (
            <FormControl fullWidth size="small">
              <InputLabel>Resolution Type *</InputLabel>
              <Select
                value={resolutionType}
                label="Resolution Type *"
                onChange={(e) => setResolutionType(e.target.value as ResolveDisputeParams['resolution_type'])}
              >
                <MenuItem value="full_refund">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Full Refund to Customer</Typography>
                    <Typography variant="caption" color="text.secondary">Customer gets 100% back. Artisan receives nothing.</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="partial_split">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Partial Split (Manual Amounts)</Typography>
                    <Typography variant="caption" color="text.secondary">You set exact amounts for each party.</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="reassign">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Refund &amp; Reassign</Typography>
                    <Typography variant="caption" color="text.secondary">Refund customer, put job back in queue for new artisan.</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="closed">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Closed — No Issue</Typography>
                    <Typography variant="caption" color="text.secondary">Misunderstanding resolved. Job continues normally.</Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Partial split amount fields */}
          {confirmAction?.type === 'resolve' && resolutionType === 'partial_split' && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Artisan Payout (₦)"
                  type="number"
                  value={artisanPayout}
                  onChange={(e) => setArtisanPayout(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                  helperText="Amount artisan receives"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Customer Refund (₦)"
                  type="number"
                  value={customerRefund}
                  onChange={(e) => setCustomerRefund(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                  helperText="Amount customer gets back"
                />
              </Grid>
              {selectedDispute?.job?.escrow_amount && artisanPayout && customerRefund && (
                <Grid item xs={12}>
                  {(() => {
                    const total = Number(artisanPayout) + Number(customerRefund);
                    const escrow = selectedDispute.job.escrow_amount || 0;
                    const diff = Math.abs(total - escrow);
                    return diff > 1 ? (
                      <Alert severity="warning" sx={{ py: 0.5 }}>
                        Split total (₦{total.toLocaleString()}) doesn't match escrow (₦{escrow.toLocaleString()}). Difference: ₦{diff.toLocaleString()}.
                      </Alert>
                    ) : (
                      <Alert severity="success" sx={{ py: 0.5 }}>Split totals match escrow ✓</Alert>
                    );
                  })()}
                </Grid>
              )}
            </Grid>
          )}

          {/* Escalation description */}
          {confirmAction?.type === 'escalate' && (
            <Typography variant="body2" color="text.secondary">
              This will flag the dispute for senior management review. Both parties will be notified that escalation is in progress.
            </Typography>
          )}

          {/* Notes */}
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={confirmAction?.type === 'resolve' ? 'Admin Notes (required)' : 'Notes (optional)'}
            placeholder={
              confirmAction?.type === 'resolve'
                ? 'Summarise your call with both parties and your reasoning…'
                : 'Add any notes…'
            }
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            size="small"
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpenConfirmDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={
              confirmAction?.type === 'escalate' ? 'info'
              : confirmAction?.type === 'close' ? 'warning'
              : 'success'
            }
            variant="contained"
            disabled={
              actionLoading ||
              (confirmAction?.type === 'resolve' && !resolutionNotes.trim())
            }
            startIcon={
              actionLoading ? <CircularProgress size={18} color="inherit" />
              : confirmAction?.type === 'escalate' ? <EscalateIcon />
              : <ResolveIcon />
            }
          >
            {actionLoading ? 'Processing…'
              : confirmAction?.type === 'resolve' ? 'Confirm & Release Escrow'
              : confirmAction?.type === 'escalate' ? 'Escalate'
              : 'Close Dispute'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
